"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Edit, Trash2, MoreHorizontal, Loader2, Eye, EyeOff, ExternalLink, Copy } from "lucide-react";

interface AddressBookEntry {
  id: number;
  user_id: number;
  peer_id: string;
  ip: string;
  name: string;
  group_name: string | null;
  password_encrypted: string | null;
  notes: string | null;
  tags: string | null;
  created_at: string;
  updated_at: string;
  password?: string | null;
}

interface AddressBookFormData {
  peer_id: string;
  ip: string;
  name: string;
  group_name: string;
  password: string;
  notes: string;
  tags: string;
}

export function AddressBookPage() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [formData, setFormData] = useState({
    peer_id: "",
    ip: "",
    name: "",
    group_name: "",
    password: "",
    notes: "",
    tags: "",
  });
  const [showPassword, setShowPassword] = useState<Record<number, boolean>>({});

  const { data: entriesData, isLoading } = useQuery({
    queryKey: ["addressBook"],
    queryFn: () => api.getAddressBook(token!),
    enabled: !!token,
    refetchInterval: 30000,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.createAddressBookEntry(data, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addressBook"] });
      setAddDialogOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.updateAddressBookEntry(id, data, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addressBook"] });
      setEditDialogOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deleteAddressBookEntry(id, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addressBook"] });
    },
  });

  const filteredEntries = entriesData?.filter(
    (entry: any) =>
      entry.name.toLowerCase().includes(search.toLowerCase()) ||
      entry.peer_id.toLowerCase().includes(search.toLowerCase()) ||
      entry.ip.toLowerCase().includes(search.toLowerCase()) ||
      entry.group_name?.toLowerCase().includes(search.toLowerCase())
  ) || [];

  const handleEdit = (entry: any) => {
    setSelectedEntry(entry);
    setFormData({
      peer_id: entry.peer_id,
      ip: entry.ip,
      name: entry.name,
      group_name: entry.group_name || "",
      password: "",
      notes: entry.notes || "",
      tags: entry.tags || "",
    });
    setEditDialogOpen(true);
  };

  const handleAdd = () => {
    resetForm();
    setAddDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleSubmitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedEntry) {
      updateMutation.mutate({ id: selectedEntry.id, data: formData });
    }
  };

  const handleBulkAdd = async () => {
    // This will be implemented to add selected devices from the Devices page
  };

  const togglePassword = (id: number) => {
    setShowPassword(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getConnectUri = (peerId: string, password?: string) => {
    return `rustdesk://${peerId}${password ? `?password=${encodeURIComponent(password)}` : ""}`;
  };

  const handleConnect = (peerId: string, password?: string) => {
    const uri = getConnectUri(peerId, password);
    window.open(uri, "_blank");
  };

  const resetForm = () => {
    setFormData({
      peer_id: "",
      ip: "",
      name: "",
      group_name: "",
      password: "",
      notes: "",
      tags: "",
    });
    setSelectedEntry(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-6">
            <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
            <div className="mt-4 h-8 w-1/2 animate-pulse rounded bg-muted" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Address Book</h1>
          <p className="text-muted-foreground">Manage your personal device connections and saved passwords</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleAdd} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Entry
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>My Devices</CardTitle>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Search devices..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="hidden sm:table-cell">Status</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="hidden md:table-cell">RustDesk ID</TableHead>
                <TableHead className="hidden sm:table-cell">IP</TableHead>
                <TableHead className="hidden lg:table-cell">Group</TableHead>
                <TableHead className="hidden sm:table-cell">Password</TableHead>
                <TableHead className="hidden lg:table-cell">Tags</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No devices in your address book. Click 'Add Entry' to get started.
                  </TableCell>
                </TableRow>
              ) : (
                filteredEntries.map((entry: any) => (
                  <TableRow key={entry.id}>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant={entry.online ? "success" : "secondary"} className="w-full">
                        {entry.online ? "Online" : "Offline"}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Badge variant={entry.online ? "success" : "secondary"} className="hidden sm:inline-flex">
                          {entry.online ? "Online" : "Offline"}
                        </Badge>
                        <span>{entry.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs hidden md:table-cell">{entry.peer_id.slice(0, 12)}...</TableCell>
                    <TableCell className="font-mono text-sm hidden sm:table-cell">{entry.ip}</TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {entry.group_name ? (
                        <Badge variant="secondary">{entry.group_name}</Badge>
                      ) : (
                        <span className="text-muted-foreground">Ungrouped</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="flex items-center gap-2">
                        {entry.password_encrypted ? (
                          <>
                            <span className="font-mono text-sm" style={{ letterSpacing: "0.1em" }}>
                              {entry.password || "••••••••"}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => copyToClipboard(entry.password || "")}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <span className="text-muted-foreground text-sm">Not set</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {entry.tags ? (
                        <span className="text-sm text-muted-foreground">{entry.tags}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleConnect(entry.peer_id, entry.password || undefined)}>
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Connect (Open RustDesk)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => copyToClipboard(entry.peer_id)}>
                            <Copy className="mr-2 h-4 w-4" />
                            Copy RustDesk ID
                          </DropdownMenuItem>
                          {entry.password && (
                            <DropdownMenuItem onClick={() => copyToClipboard(entry.password || "")}>
                              <Copy className="mr-2 h-4 w-4" />
                              Copy Password
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleEdit(entry)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              if (window.confirm("Delete this entry?")) {
                                deleteMutation.mutate(entry.id);
                              }
                            }}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add to Address Book</DialogTitle>
            <DialogDescription>Add a new device to your personal address book</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="My Work Laptop"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="peer_id">RustDesk ID *</Label>
                  <Input
                    id="peer_id"
                    value={formData.peer_id}
                    onChange={(e) => setFormData({ ...formData, peer_id: e.target.value })}
                    placeholder="1234567890"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ip">IP Address</Label>
                  <Input
                    id="ip"
                    value={formData.ip}
                    onChange={(e) => setFormData({ ...formData, ip: e.target.value })}
                    placeholder="192.168.1.100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="group_name">Group</Label>
                  <Input
                    id="group_name"
                    value={formData.group_name}
                    onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                    placeholder="Work, Personal, Servers"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password (will be encrypted)</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Optional: connection password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tags">Tags (comma separated)</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="e.g. work, linux, ssh"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes about this device"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setAddDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add Device"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Device</DialogTitle>
            <DialogDescription>Update device information and connection details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitEdit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name *</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-peer_id">RustDesk ID *</Label>
                  <Input
                    id="edit-peer_id"
                    value={formData.peer_id}
                    onChange={(e) => setFormData({ ...formData, peer_id: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-ip">IP Address</Label>
                  <Input
                    id="edit-ip"
                    value={formData.ip}
                    onChange={(e) => setFormData({ ...formData, ip: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-group_name">Group</Label>
                  <Input
                    id="edit-group_name"
                    value={formData.group_name}
                    onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-password">Connection Password</Label>
                <Input
                  id="edit-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Leave empty to keep current password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <textarea
                  id="edit-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-tags">Tags (comma separated)</Label>
                <Input
                  id="edit-tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setEditDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}